"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  ShoppingCart, 
  Calendar, 
  MessageSquare, 
  Newspaper, 
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Bot,
  FolderTree,
  FileText,
  Archive
} from "lucide-react";
import { useState, useEffect } from "react";
import authService from "../../../../services/authService";
import permissionService from "../../../../services/permissionService";
import styles from "./Sidebar.module.css";

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
  isOpen?: boolean; // For mobile sidebar open/close state
}

interface MenuItem {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  href: string;
  active: boolean;
  requiredPermission?: string; // Permission code cần thiết để hiển thị menu này
}

export default function Sidebar({ isCollapsed = false, onToggle, onCollapseChange, isOpen }: SidebarProps) {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const admin = await authService.getCurrentAdmin();
        if (admin) {
          setUserRole(admin.role);
          
          // Load permissions của user
          const permissionsRes = await permissionService.getMyPermissions();
          setUserPermissions(permissionsRes.data.permissions);
          setIsAdmin(permissionsRes.data.isAdmin);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    fetchUserInfo();
  }, []);

  // Sync collapsed state with prop
  useEffect(() => {
    if (!isMobile) {
      setCollapsed(isCollapsed);
    }
  }, [isCollapsed, isMobile]);

  const toggleSidebar = () => {
    if (!isMobile) {
      setCollapsed(!collapsed);
      onCollapseChange?.(!collapsed);
    }
    onToggle?.();
  };
  
  // Close sidebar when clicking a link on mobile
  const handleLinkClick = () => {
    if (isMobile) {
      onToggle?.();
    }
  };

  const allMenuItems: MenuItem[] = [
    {
      icon: LayoutDashboard,
      label: "Tổng quan",
      href: "/admin/dashboard",
      active: pathname === "/admin/dashboard" || pathname === "/admin",
      requiredPermission: "dashboard.view"
    },
    {
      icon: Newspaper,
      label: "Quản lý tin tức",
      href: "/admin/news/list",
      active: pathname.startsWith("/admin/news"),
      requiredPermission: "news.view"
    },
    {
      icon: FolderTree,
      label: "Quản lý danh mục",
      href: "/admin/categories",
      active: pathname.startsWith("/admin/categories"),
      requiredPermission: "categories.view"
    },
    {
      icon: FileText,
      label: "Điều khoản & Chính sách",
      href: "/admin/policies",
      active: pathname.startsWith("/admin/policies"),
      requiredPermission: "policies.view"
    },
    {
      icon: Package,
      label: "Quản lý sản phẩm",
      href: "/admin/products/list",
      active: pathname.startsWith("/admin/products/list"),
      requiredPermission: "products.view"
    },
    {
      icon: Briefcase,
      label: "Quản lý dịch vụ",
      href: "/admin/services",
      active: pathname.startsWith("/admin/services"),
      requiredPermission: "services.view"
    },
    {
      icon: Users,
      label: "Quản lý người dùng",
      href: "/admin/users",
      active: pathname.startsWith("/admin/users") && !pathname.startsWith("/admin/users/trash"),
      requiredPermission: "users.view"
    },
    {
      icon: ShoppingCart,
      label: "Đơn hàng",
      href: "/admin/orders",
      active: pathname.startsWith("/admin/orders"),
      requiredPermission: "orders.view"
    },
    {
      icon: Calendar,
      label: "Đặt lịch",
      href: "/admin/bookings",
      active: pathname.startsWith("/admin/bookings"),
      requiredPermission: "bookings.view"
    },
    {
      icon: MessageSquare,
      label: "Tin nhắn",
      href: "/admin/chat",
      active: pathname.startsWith("/admin/chat") && !pathname.startsWith("/admin/chatbot"),
      requiredPermission: "chat.view"
    },
    {
      icon: Bot,
      label: "Chat Bot",
      href: "/admin/chatbot",
      active: pathname.startsWith("/admin/chatbot"),
      requiredPermission: "chat.manage"
    },
    {
      icon: Archive,
      label: "Thùng rác",
      href: "/admin/trash",
      active: pathname.startsWith("/admin/trash") || pathname.includes("/trash"),
      requiredPermission: "trash" // Sẽ được xử lý đặc biệt - kiểm tra bất kỳ permission trash nào
    }
  ];

  // Filter menu items dựa trên permissions
  // Admin có tất cả quyền, staff chỉ thấy menu mà họ có permission
  const menuItems = allMenuItems.filter(item => {
    // Xử lý đặc biệt cho menu Thùng rác - hiển thị nếu có ít nhất một quyền xem trash
    if (item.requiredPermission === "trash") {
      if (isAdmin) return true;
      // Kiểm tra xem user có ít nhất một trong các quyền xem trash
      // Sử dụng format cũ: news.trash.view
      const trashPermissions = [
        "news.trash.view", "categories.trash.view", "policies.trash.view",
        "products.trash.view", "services.trash.view", "users.trash.view"
      ];
      return trashPermissions.some(perm => userPermissions.includes(perm));
    }
    
    // Nếu không có requiredPermission, hiển thị cho tất cả
    if (!item.requiredPermission) return true;
    
    // Admin có tất cả quyền
    if (isAdmin) return true;
    
    // Staff chỉ thấy menu mà họ có permission
    return userPermissions.includes(item.requiredPermission);
  });

  return (
    <aside 
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${isOpen ? styles.open : ''}`}
      onMouseEnter={() => { 
        if (!isMobile) {
          setCollapsed(false); 
          onCollapseChange?.(false); 
        }
      }}
      onMouseLeave={() => { 
        if (!isMobile) {
          setCollapsed(true); 
          onCollapseChange?.(true); 
        }
      }}
    >
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarLogo}>
          <Image
            src="/assets/sdtc-image/trang-chu/Logo/sdtc.webp"
            alt="Logo SDTC"
            width={isMobile ? 120 : (collapsed ? 40 : 120)}
            height={isMobile ? 50 : (collapsed ? 40 : 50)}
            priority
            className={styles.logoImage}
          />
        </div>
      </div>

      <nav className={styles.sidebarNav}>
        <ul className={styles.sidebarMenu}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.href} className={styles.sidebarMenuItem}>
                <Link 
                  href={item.href as any}
                  className={`${styles.sidebarMenuLink} ${item.active ? styles.active : ''}`}
                  title={collapsed && !isMobile ? item.label : undefined}
                  onClick={handleLinkClick}
                >
                  <Icon size={20} className={styles.sidebarMenuIcon} />
                  {(!collapsed || isMobile) && (
                    <span className={styles.sidebarMenuText}>{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

